/** True when a string looks like an email stored in the name field. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** Validate a member display name (full name shown on board, distinct from @handle). */
export function validateDisplayName(name: string, handle: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length < 2) return 'Display name must be at least 2 characters';
  if (!/\s/.test(trimmed)) return 'Use your full name (e.g. Joe Kimani)';
  const normalizedHandle = handle.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
  if (normalizedHandle && trimmed.toLowerCase().replace(/\s+/g, '') === normalizedHandle) {
    return 'Display name must be different from your username';
  }
  if (looksLikeEmail(trimmed)) return 'Display name cannot be an email address';
  return null;
}

/** Public display name — never prefer email; fall back to handle, then a generic label. */
export function memberDisplayName(m: {
  name?: string | null;
  handle?: string | null;
  email?: string | null;
}): string {
  const name = (m.name ?? '').trim();
  if (name && !looksLikeEmail(name)) return name;

  const handle = (m.handle ?? '').trim();
  if (handle) return handle;

  if (name && looksLikeEmail(name)) {
    const local = name.split('@')[0]?.replace(/[._]/g, ' ').trim();
    if (local) return local.charAt(0).toUpperCase() + local.slice(1);
  }

  return 'Member';
}

/** First name token for compact UI (ticker, wagers). */
export function memberFirstName(m: Parameters<typeof memberDisplayName>[0]): string {
  return memberDisplayName(m).split(/\s+/)[0] || 'Member';
}

export function normalizeMemberPublic<T extends { name?: string | null; handle?: string | null }>(m: T): T {
  return { ...m, name: memberDisplayName(m) };
}

/** Short label for tight UI (scorecard rows, chips). */
export function playerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  const single = parts[0] || '?';
  return single.slice(0, 3).toUpperCase();
}

export function displayPlayerName(
  name: string,
  opts?: { you?: boolean; maxChars?: number },
): string {
  if (opts?.you) return 'You';
  const max = opts?.maxChars ?? 14;
  if (name.length <= max) return name;
  return playerInitials(name);
}

/** Column header for game standings value column. */
export function standingsValueLabel(formatId: string): string {
  if (formatId === 'stableford') return 'Pts';
  if (formatId === 'stroke' || formatId === 'net-stroke') return 'To par';
  return 'Score';
}
